import { Formik, } from 'formik';
import { isEmpty, isNil } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import Notifications from '../../../../common/utils/Notifications';
import { handleFormErrors } from '../../../errors';
import AddModeForm from '../../utils/AddMode/AddModeForm';
import FormTitle from '../../utils/FormTitle';
import useTranslation from '../../utils/Translations/useTranslation';
import { useEditSession } from '../EditSessionProvider';
import EntityFormBody from './EntityFormBody';
import EntityStrike from './EntityStrike';
function EntityForm({ className, title = '', onGoBack, onDelete, onCancel, createLabel, cancelLabel = 'Cancel', updateLabel, deleteLabel, goBackLabel = 'Go Back', columns, fieldWrapperClassNames, onSubmit, onSubmitFinished, submitSectionRenderer, validate, successMessageText = 'Created Successfully', updateMessageText = 'Updated Successfully', hasUpdateMessage = true, hasCreateMessage = true, submitActions, isAlwaysEditable, onStrikeClick, buttonTitle, strikeIcon, customErrorExtract, isReadOnly = false, isSubmitDisabled, isCopied, hasResetOnCancel = false, entity, entityName, children, innerRef, initialTouched, validateOnMount, isSubmitTogglesDropdown, hasValidateOnBlur = true, hasValidateOnChange = true, hasClearFormButton = false, defaultEntity = {}, promptMessage, hasLeavePrompt, leftBottomSection, isStoppedEventOutsideForm, onLeave, ignoreAddMode = false, enableReinitialize = true, hasSubmitOnChange = false, pullLeft, submitContainerClass, submitClass, isCancelDisabled = false, floatButton = false, submitBtnAdditionClasses, ...props }) {
    const { isEditSessionActive } = useEditSession();
    const { t, direction } = useTranslation();
    const defaultCreateLabel = t('Create');
    const defaultUpdateLabel = t('Update');
    const isNew = useMemo(() => (props.isNew === undefined ? isNil(entity === null || entity === void 0 ? void 0 : entity.id) : props.isNew), [entity, props.isNew]);
    const isEditing = useMemo(() => {
        if (isReadOnly)
            return false;
        if (isAlwaysEditable)
            return true;
        return isEditSessionActive;
    }, [isAlwaysEditable, isEditSessionActive, isReadOnly]);
    const renderMessage = useCallback(_isNew => {
        if (_isNew) {
            hasCreateMessage && Notifications.success(t(successMessageText), '', t);
        }
        else {
            hasUpdateMessage && Notifications.success(t(updateMessageText), '', t);
        }
    }, [
        hasCreateMessage,
        hasUpdateMessage,
        successMessageText,
        t,
        updateMessageText,
    ]);
    const handleSubmit = useCallback(async (values, formikHelpers) => {
        try {
            const result = await handleFormErrors(onSubmit && onSubmit(values, formikHelpers), t, undefined, customErrorExtract);
            if (result !== false) {
                renderMessage(isNil(values === null || values === void 0 ? void 0 : values.id));
                onSubmitFinished && onSubmitFinished(result);
            }
            return result;
        }
        catch (e) {
            /* ignore */
            console.warn(e);
        }
    }, [customErrorExtract, onSubmit, onSubmitFinished, renderMessage, t]);
    const handleClick = useCallback(e => {
        if (isStoppedEventOutsideForm) {
            e.stopPropagation();
        }
    }, [isStoppedEventOutsideForm]);
    const form = useMemo(() => (<div className={className} data-layout-direction={direction} dir={direction} onClick={handleClick}>
        {title && !onStrikeClick ? (<FormTitle title={title}/>) : (title && (<EntityStrike isPageTitle buttonTitle={buttonTitle} icon={strikeIcon} title={title} onClick={onStrikeClick}/>))}

        <Formik enableReinitialize={enableReinitialize} initialTouched={initialTouched} initialValues={entity} innerRef={innerRef} validate={validate} validateOnBlur={hasValidateOnBlur} validateOnChange={hasValidateOnChange} validateOnMount={isNil(validateOnMount) ? !isNew : validateOnMount} onSubmit={handleSubmit}>
          {({ values, dirty, isValid, submitForm, isSubmitting, resetForm, errors, }) => (<EntityFormBody cancelLabel={cancelLabel} columns={columns} createLabel={createLabel || defaultCreateLabel} defaultEntity={defaultEntity} deleteLabel={deleteLabel} dirty={dirty} entity={entity} entityName={entityName} fieldWrapperClassNames={fieldWrapperClassNames} floatButton={floatButton} goBackLabel={goBackLabel} hasClearFormButton={hasClearFormButton} hasErrors={!isEmpty(errors)} hasLeavePrompt={hasLeavePrompt} hasResetOnCancel={hasResetOnCancel} hasSubmitOnChange={hasSubmitOnChange} isCancelDisabled={isCancelDisabled} isCopied={isCopied} isEditing={isEditing} isNew={isNew} isSubmitDisabled={isSubmitDisabled} isSubmitting={isSubmitting} isSubmitTogglesDropdown={isSubmitTogglesDropdown} isValid={isValid} leftBottomSection={leftBottomSection} promptMessage={promptMessage} pullLeft={pullLeft} resetForm={resetForm} submitActions={submitActions} submitBtnAdditionClasses={submitBtnAdditionClasses} submitClass={submitClass} submitContainerClass={submitContainerClass} submitForm={submitForm} submitSectionRenderer={submitSectionRenderer} updateLabel={updateLabel || defaultUpdateLabel} values={values} onCancel={onCancel} onDelete={onDelete} onGoBack={onGoBack} onLeave={onLeave}>
              {children}
            </EntityFormBody>)}
        </Formik>
      </div>), [
        direction,
        deleteLabel,
        hasSubmitOnChange,
        pullLeft,
        submitContainerClass,
        submitClass,
        enableReinitialize,
        handleClick,
        promptMessage,
        leftBottomSection,
        hasLeavePrompt,
        buttonTitle,
        className,
        children,
        columns,
        createLabel,
        defaultCreateLabel,
        cancelLabel,
        entity,
        entityName,
        fieldWrapperClassNames,
        handleSubmit,
        isNew,
        isSubmitDisabled,
        onCancel,
        onDelete,
        onGoBack,
        onStrikeClick,
        strikeIcon,
        submitActions,
        submitSectionRenderer,
        title,
        updateLabel,
        defaultUpdateLabel,
        validate,
        isEditing,
        hasResetOnCancel,
        goBackLabel,
        innerRef,
        initialTouched,
        validateOnMount,
        isSubmitTogglesDropdown,
        hasValidateOnChange,
        hasValidateOnBlur,
        hasClearFormButton,
        defaultEntity,
        onLeave,
        floatButton,
        submitBtnAdditionClasses,
    ]);
    return isNew ? (<AddModeForm ignoreAddMode={ignoreAddMode}>{form}</AddModeForm>) : (form);
}
export default EntityForm;
